import { useMutation, useQueryClient } from "@tanstack/react-query";

import { CALC_KEY, CASES_KEY, runCalculation, StaleVersionError, type CalculationIn } from "@/api";

import { refetchCase } from "./refetchCase";

/**
 * Where `EmptyCalculation` leaves the confirmation of the very first run for
 * `CalculationScreen` to pick up (S-3): the route swaps components the
 * instant that run lands, unmounting the empty state's own live region
 * before it could ever announce anything the run just did. Every later run
 * happens on one already-mounted `CalculationScreen`, whose own `statusLine`
 * state carries the word across the rerun directly — this key exists for
 * the one swap that state cannot reach across.
 */
export const RUN_STATUS_KEY = (caseId: string) => ["calc-run-status", caseId] as const;

/**
 * One run of the engine, from the empty state or from the screen.
 *
 * On success the answer is written straight into the calculation's cache
 * entry — the route re-renders with it, no second GET for it. **But only
 * once the Vorgang has been read again** (`["case", id]`, exact — not the
 * prefix, or this very entry would be refetched too): the run moved its
 * status (`calculation_ready` / `price_data_missing`, D-11), and a current
 * calculation beside the old „in planning" is exactly what the banner „Erst
 * neu rechnen" stands for — it flashed for the length of that refetch right
 * after every „Neu rechnen" (Zweitgutachten 23.10.2026, offener Punkt 4).
 * Waited for, not guessed: the client does not derive the server's stage.
 * Until then the run stays pending, so the button stays busy — at most
 * `CASE_REFETCH_WAIT_MS` (review 13/14, M-1): a Vorgang that never answers
 * does not hold the answer back, nor does a refetch that fails. The start
 * page's list is invalidated alongside, not waited for.
 *
 * On the lock's 409 both are invalidated instead: the server is somewhere
 * else, and the screen has to read where before it offers „Neu rechnen".
 */
export function useRunCalculation(caseId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CalculationIn) => runCalculation(caseId, input),
    onSuccess: async ({ calc }) => {
      void client.invalidateQueries({ queryKey: CASES_KEY });
      await refetchCase(client, caseId);
      client.setQueryData(CALC_KEY(caseId), calc);
    },
    onError: (error) => {
      if (error instanceof StaleVersionError) {
        void client.invalidateQueries({ queryKey: ["case", caseId], exact: true });
        void client.invalidateQueries({ queryKey: CALC_KEY(caseId) });
      }
    },
  });
}
